import React, {useEffect, useRef, useState} from "react";
import {useSetting} from "../../contexts/setting";
import {getTitleFontSizeFromTemplate, titleSquareSize} from "../../libs/pixelationSizing";
import PixelDirectionControl, {PIXELATION_DIRECTION_LABELS} from "./PixelDirectionControl";

// Matches the floor in titleSquareSize so the override can't push the mosaic
// below a drawable square size.
const MIN_SIZE = 6;
// Matches the global Coverage slider range in PixelationControls.
const MIN_COVERAGE = 5;
const MAX_COVERAGE = 100;

/**
 * Round button (checkerboard icon) shown for an unchained (locked) Releases
 * template while the pixelation overlay is on. Opens a native popover with all
 * of this template's per-banner mosaic controls: tile size, coverage, and fade
 * direction (rotate). Each is saved to the scene under its own per-template
 * override map (`pixelationSizeOverrides` / `pixelationCoverageOverrides` /
 * `pixelationDirectionOverrides`) and honoured only while the template is
 * locked. "Use defaults" clears all three so the template follows the global
 * mosaic settings again. NOTE: pinning size/coverage detaches this template from
 * the shared pattern by design.
 */
const PixelSizeControl = ({id}: {id: string}) => {
  const {
    pixelationSizeOverrides,
    pixelationCoverageOverrides,
    pixelationDirectionOverrides,
    pixelationDirection,
    pixelationSizeCoefficient,
    pixelationCoverage,
    editPixelationSizeOverride,
    editPixelationCoverageOverride,
    resetPixelationTemplate,
  } = useSetting();
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [value, setValue] = useState("");

  const sizeOverride = pixelationSizeOverrides?.[id];
  const hasSizeOverride = sizeOverride > 0;
  const coverageOverride = pixelationCoverageOverrides?.[id];
  const directionOverride = pixelationDirectionOverrides?.[id];
  // The trigger button lights up (like an engaged lock) whenever this template
  // carries any per-banner override at all.
  const hasAnyOverride = hasSizeOverride || coverageOverride > 0 || !!directionOverride;
  // Coverage the template actually renders at: its own pin, else the global.
  const coverage = coverageOverride > 0 ? coverageOverride : pixelationCoverage;
  const direction = directionOverride || pixelationDirection;
  const popoverId = `pixel-size-popover-${id}`;

  // The default tile size the template renders at with no override, matching
  // PixelationLayer: the template's title font size × coefficient. Read from
  // this banner's live `.title`. Independent of direction and coverage.
  const computeDefaultSize = () => {
    const templateEl = document.getElementById(id) as HTMLElement | null;
    return titleSquareSize(getTitleFontSizeFromTemplate(templateEl), pixelationSizeCoefficient);
  };

  // Native popover renders centered in the top layer by default; anchor it just
  // below the button instead. Positioned on `beforetoggle` (before paint) so it
  // never flashes in the wrong spot.
  useEffect(() => {
    const pop = popoverRef.current;
    if (!pop) return;
    const onBeforeToggle = (e: Event) => {
      if ((e as ToggleEvent).newState !== "open") return;
      const btn = buttonRef.current;
      if (btn) {
        const rect = btn.getBoundingClientRect();
        const popWidth = pop.offsetWidth || 220;
        const left = Math.max(8, Math.min(rect.left, window.innerWidth - popWidth - 8));
        pop.style.top = `${rect.bottom + 8}px`;
        pop.style.left = `${left}px`;
      }
      setValue(String(hasSizeOverride ? sizeOverride : computeDefaultSize()));
    };
    pop.addEventListener("beforetoggle", onBeforeToggle);
    return () => pop.removeEventListener("beforetoggle", onBeforeToggle);
    // Re-bind when the size override or coefficient changes so the prefilled
    // tile-size value reflects the latest state on the next open. (Direction
    // doesn't affect the tile size, so it's intentionally not a dependency.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizeOverride, hasSizeOverride, pixelationSizeCoefficient, id]);

  const commitSize = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed === "") {
      editPixelationSizeOverride(id, null);
      return;
    }
    const n = Math.round(Number(trimmed));
    if (Number.isFinite(n) && n >= MIN_SIZE) {
      editPixelationSizeOverride(id, n);
    }
  };

  const handleSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    commitSize(e.target.value);
  };

  const handleResetAll = () => {
    resetPixelationTemplate(id);
    setValue(String(computeDefaultSize()));
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`checkbox pixel-size${hasAnyOverride ? " active" : ""}`}
        data-tooltip="Mosaic settings for this template"
        popoverTarget={popoverId}
        aria-label="Mosaic settings for this template"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <rect x="2" y="2" width="7" height="7" fill="currentColor" />
          <rect x="11" y="11" width="7" height="7" fill="currentColor" />
          <rect x="11" y="2" width="7" height="7" fill="currentColor" opacity="0.35" />
          <rect x="2" y="11" width="7" height="7" fill="currentColor" opacity="0.35" />
        </svg>
      </button>
      {/* The popover is a DOM descendant of the template's @dnd-kit sortable
          node (the whole banner is a drag handle). Without this, a pointerdown
          on the coverage slider bubbles to that node and, once the thumb moves
          past the 5px activation distance, starts dragging/reordering the
          template. Stop pointerdown here so controls inside the popover never
          trigger a template drag. */}
      <div
        ref={popoverRef}
        id={popoverId}
        popover="auto"
        className="pixel-size-popover"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <label className="pixel-size-popover__label" htmlFor={`pixel-size-input-${id}`}>
          Mosaic tile size
        </label>
        <div className="pixel-size-popover__row">
          <input
            id={`pixel-size-input-${id}`}
            type="number"
            min={MIN_SIZE}
            step={1}
            value={value}
            onChange={handleSizeChange}
            className="pixel-size-popover__input"
          />
          <span className="pixel-size-popover__unit">px</span>
        </div>

        <div className="pixel-size-popover__section">
          <label className="pixel-size-popover__label" htmlFor={`pixel-coverage-input-${id}`}>
            Coverage
          </label>
          <div className="pixel-size-popover__row">
            <input
              id={`pixel-coverage-input-${id}`}
              type="range"
              min={MIN_COVERAGE}
              max={MAX_COVERAGE}
              step={1}
              value={coverage}
              onChange={(e) => editPixelationCoverageOverride(id, Number(e.target.value))}
              className="pixel-size-popover__slider"
            />
            <span className="pixel-size-popover__unit">{coverage}%</span>
          </div>
        </div>

        <div className="pixel-size-popover__section">
          <span className="pixel-size-popover__label">Direction</span>
          <div className="pixel-size-popover__row">
            <span className="pixel-size-popover__direction-label">
              {PIXELATION_DIRECTION_LABELS[direction]}
            </span>
            <PixelDirectionControl id={id} />
          </div>
        </div>

        <button type="button" className="pixel-size-popover__reset" onClick={handleResetAll}>
          Use defaults
        </button>
      </div>
    </>
  );
};

export default PixelSizeControl;
